import { FC, HTMLAttributes, useEffect, useState, useMemo } from "react";
import clsx from "classnames";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { meGQL, getConfigGQL, achievementsGQL } from "@onmo/graphql/graphql";
import { StaticImage } from "../StaticImage";
import { DailyRewardHeader } from "../DailyRewardHeader";
import { YourRewardCard } from "../YourRewardCard";
import { DailyRewardCategory } from "../DailyRewardCategory";
import { CoinsAnimation } from "../CoinsAnimation";
import Button from "../../core/Button/Button";
import Bg from "../../../assets/endGame/bg.webp";
import Bg2x from "../../../assets/endGame/bg@2x.webp";
import Bg3x from "../../../assets/endGame/bg@3x.webp";
import { User } from "../../../models/user";
import { AchievementReward } from "../AchievementReward";
import { PageRoutes } from "../../../constants/historyGaming";
import { DURATION_COIN_ANIMATION } from "../../../constants/animation";
import { getDifferentAchievements } from "../../../models/achievement";
import { LevelUpAnimation } from "../LevelUpAnimation";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../hooks";
import { PRODUCT_QUERY } from "../../../constants/achievement";
import { useStyles } from "./RewardScreen.style";

interface IRewardScreenProps {
  rewardAmount: number;
}

export const RewardScreen: FC<
  HTMLAttributes<HTMLDivElement> & IRewardScreenProps
> = (props) => {
  const classes = useStyles();
  const { rewardAmount, className, ...otherProps } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { achievements } = useAppSelector((state) => state.user);
  const { data: dataMe } = useQuery(meGQL);
  const { data } = useQuery(achievementsGQL, {
    variables: {
      input: { products: PRODUCT_QUERY },
    },
  });
  const { data: dataConfig } = useQuery(getConfigGQL);
  const userRank = User.getCurrentRank(
    dataMe?.me?.coinWallets,
    dataConfig?.config.tierLevels,
  );

  const isRankUp = useMemo(() => {
    if (!userRank?.nextLevel || !dataMe) return false;
    return userRank.xp - rewardAmount < userRank.currentLevelXp;
  }, [userRank, rewardAmount, dataMe]);

  const [showAchievement, setShowAchievement] = useState(false);
  const [showCoin, setShowCoin] = useState(false);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const differentAchievements = getDifferentAchievements(
    achievements || [],
    data?.me?.achievements?.items,
  );

  const onReturnHome = () => {
    navigate(PageRoutes.HomePage, { replace: true });
  };

  const onViewAchievements = () => {
    navigate(PageRoutes.Profile, { replace: true });
  };

  const onShowLevelUp = () => {
    setShowLevelUp(true);
    setShowAchievement(false);
  };

  useEffect(() => {
    const timeoutShowCoin = setTimeout(() => {
      setShowCoin(true);
    }, 100);
    const timeoutShowAchievement = setTimeout(() => {
      if (differentAchievements?.length > 0 && !showLevelUp) {
        setShowAchievement(true);
      }
      if (!differentAchievements?.length && isRankUp) {
        setShowLevelUp(true);
      }
    }, DURATION_COIN_ANIMATION + 100);
    return () => {
      clearTimeout(timeoutShowCoin);
      clearTimeout(timeoutShowAchievement);
    };
  }, [differentAchievements, showLevelUp, isRankUp]);

  return (
    <div {...otherProps} className={clsx(classes.root, className)}>
      <StaticImage
        src={Bg}
        src2x={Bg2x}
        src3x={Bg3x}
        className={classes.bgImage}
      />
      <div className={classes.content}>
        <DailyRewardHeader
          idCoin="coin-target"
          nbCoin={User.getGzCoin(dataMe?.me?.coinWallets)}
        />
        <div className={classes.main}>
          <div>
            <YourRewardCard
              className={classes.rewardCard}
              coinReward={rewardAmount}
            />
            <DailyRewardCategory
              wallets={dataMe?.me?.coinWallets}
              newAchievement={differentAchievements[0]}
              tierLevels={dataConfig?.config.tierLevels}
            />
          </div>
          <div className={classes.buttonGroup}>
            <Button fullWidth onClick={onReturnHome}>
              {t("common_Return Home")}
            </Button>
            <Button fullWidth variant="outlined" onClick={onViewAchievements}>
              {t("common_View Achievements")}
            </Button>
          </div>
        </div>
      </div>
      {showCoin && <CoinsAnimation />}
      {showAchievement && differentAchievements[0]?.currentTier > 0 && (
        <AchievementReward
          newAchievement={differentAchievements[0]}
          nbCoin={User.getGzCoin(dataMe?.me?.coinWallets)}
          onShowLevelUp={onShowLevelUp}
        />
      )}
      {showLevelUp && isRankUp && <LevelUpAnimation />}
    </div>
  );
};
